import { createFormHook, revalidateLogic } from '@tanstack/react-form';
import * as v from 'valibot';

import { fieldContext, formContext } from './app_form_context';
import { ArrayTextField } from './field_components/array_text_field';
import { CheckboxField } from './field_components/checkbox_field';
import { CheckboxFieldGroup } from './field_components/checkbox_field_group';
import { NumberField } from './field_components/number_field';
import { SelectField } from './field_components/select_field';
import { SwitchField } from './field_components/switch_field';
import { TagField } from './field_components/tag_field';
import { TextAreaField } from './field_components/text_area_field';
import { TextField } from './field_components/text_field';
import { CancelButton } from './form_components/cancel_button';
import { FormActions } from './form_components/form_actions';
import { SubmitButton } from './form_components/submit_button';

const fieldComponents = {
  ArrayTextField,
  CheckboxField,
  CheckboxFieldGroup,
  NumberField,
  SelectField,
  SwitchField,
  TagField,
  TextAreaField,
  TextField,
};

const formComponents = {
  FormActions,
  CancelButton,
  SubmitButton,
};

const { useAppForm: useTanStackAppForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents,
  formComponents,
});

type UseAppFormParams<TSchema extends v.GenericSchema> = {
  /** Valibot schema used for validation and output transformation. */
  schema: TSchema;
  /** Initial values for all form fields. Must match the schema's input type. */
  defaultValues: v.InferInput<TSchema>;
  /** Called with the validated and transformed values after successful submission. */
  onSubmit: (values: v.InferOutput<TSchema>) => void | Promise<void>;
};

/**
 * Create a form instance with Valibot schema validation and pre-bound UAD field components.
 *
 * For form actions, prefer the provided components inside `<form.AppForm>`:
 * - `form.FormActions` for standard action row layout
 * - `form.SubmitButton` for submit state wiring (`isSubmitting`)
 * - `form.CancelButton` to reset form state before running custom cancel handlers
 *
 * Avoid manually wiring submit pending state with `<form.Subscribe>` when these helpers fit.
 *
 * @example
 * ```tsx
 * import * as v from 'valibot';
 * import { Form } from '@iconik-io/uad';
 * import { useAppForm } from '@iconik-io/shared-lib/src/react/hooks/form/use_app_form';
 *
 * const schema = v.object({
 *   name: v.pipe(v.string(), v.nonEmpty('Name is required')),
 *   email: v.pipe(v.string(), v.email('Must be a valid email')),
 * });
 *
 * function MyForm() {
 *   const form = useAppForm({
 *     schema,
 *     defaultValues: { name: '', email: '' },
 *     onSubmit: async (values) => {
 *       // values is typed as v.InferOutput<typeof schema>
 *       await saveUser(values);
 *     },
 *   });
 *
 *   return (
 *     <Form validationBehavior="aria" onSubmit={(event) => {
 *       event.preventDefault();
 *       void form.handleSubmit();
 *     }}>
 *       <form.AppField name="name">
 *         {(field) => <field.TextField label="Name" isRequired />}
 *       </form.AppField>
 *       <form.AppField name="email">
 *         {(field) => <field.TextField label="Email" isRequired />}
 *       </form.AppField>
 *       <form.AppForm>
 *         <form.FormActions>
 *           <form.CancelButton onPress={() => closeDialog()}>
 *             Cancel
 *           </form.CancelButton>
 *           <form.SubmitButton>Save</form.SubmitButton>
 *         </form.FormActions>
 *       </form.AppForm>
 *     </Form>
 *   );
 * }
 * ```
 */
export function useAppForm<TSchema extends v.GenericSchema>({
  schema,
  defaultValues,
  onSubmit,
}: UseAppFormParams<TSchema>) {
  return useTanStackAppForm({
    // Validate on submit, then on every change once the user has tried to
    // submit. Do not move the schema to an `onSubmit` validator. TanStack
    // drops a submit-cause error as soon as any other validation pass reports
    // nothing (see FieldApi.validateSync), and blur runs such a pass, so the
    // first click outside a field would erase its message.
    validationLogic: revalidateLogic(),
    defaultValues,
    validators: {
      onDynamic: schema,
    },
    onSubmitInvalid() {
      const InvalidInput = document.querySelector<HTMLInputElement>(
        '[aria-invalid="true"]',
      );

      InvalidInput?.focus();
    },
    onSubmit: async ({ value }) => {
      const result = v.parse(schema, value);
      await onSubmit(result);
    },
  });
}
